import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@refinedev/core';
import { notificationQueryKeys } from 'api/resource/notification';
import { claimQueryResourceKeys } from 'api/resource/claimQuery';
import { claimQueryKeys } from 'api/resource/claim';
import { remittanceQueryKeys } from 'api/resource/remittance';
import { reportQueryKeys } from 'api/resource/report';
import { submissionQueryKeys } from 'api/resource/submission';
import { intakeItemQueryKeys } from 'api/resource/intakeItem';
import { subscribeToNotificationEvents } from 'api/ws/notifications';
import { subscribeToWebSocketConnection } from 'api/ws/session';
import { getNotificationText } from './notificationText';

export const NotificationSyncManager = () => {
  const queryClient = useQueryClient();
  const { translate } = useTranslation();

  useEffect(() => {
    const unsubscribeMessages = subscribeToNotificationEvents(
      ({ notification }) => {
        void queryClient.invalidateQueries({
          queryKey: notificationQueryKeys.all,
        });

        switch (notification.type) {
          case 'report.ready':
            void queryClient.invalidateQueries({
              queryKey: reportQueryKeys.all,
            });
            break;
          case 'submission.ready':
          case 'submission.failed':
            void queryClient.invalidateQueries({
              queryKey: submissionQueryKeys.all,
            });
            break;
          case 'remittance.completed':
          case 'remittance.failed':
            void queryClient.invalidateQueries({
              queryKey: remittanceQueryKeys.listRemittanceBySite(
                notification.data.siteID
              ),
            });
            break;
          case 'claimQuery.completed':
            void queryClient.invalidateQueries({
              queryKey: claimQueryResourceKeys.all,
            });
            void queryClient.invalidateQueries({
              queryKey: claimQueryKeys.all,
            });
            break;
          case 'intakeItem.assigned':
            void queryClient.invalidateQueries({
              queryKey: intakeItemQueryKeys.all,
            });
            break;
        }

        const text = getNotificationText(translate, notification);
        notifications.show({
          id: notification.id,
          title: text.title,
          message: text.body,
          color: notification.severity === 'error' ? 'red' : 'teal',
        });
      }
    );

    const unsubscribeConnection = subscribeToWebSocketConnection(() => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
      void queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: submissionQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: remittanceQueryKeys.all });
      void queryClient.invalidateQueries({
        queryKey: claimQueryResourceKeys.all,
      });
      void queryClient.invalidateQueries({ queryKey: claimQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: intakeItemQueryKeys.all });
    });

    return () => {
      unsubscribeMessages();
      unsubscribeConnection();
    };
  }, [queryClient, translate]);

  return null;
};
